import { apiClient } from './apiService';
import {
  CUSTOMER_MEMBERSHIPS,
  CUSTOMER_POINTS_HISTORY,
  CUSTOMER_STAMP_CARDS,
  CUSTOMER_SUMMARY,
  CUSTOMER_VOUCHERS,
  LOYALTY_POINTS_ADD,
  VOUCHER_GRANT,
  VOUCHER_TEMPLATES_LIST
} from './endpoints';
import type {
  CustomerMembership,
  CustomerMembershipTransaction,
  CustomerStampProgress,
  CustomerSummary,
  CustomerVoucher,
  PointsTransaction,
  VoucherTemplateOption
} from '@/types/Customer360.interface';

const withId = (path: string, id: number) => path.replace(':id', String(id));

/** The admin customer card's data and its two staff actions. */
export const customer360Service = {
  async getSummary(userId: number): Promise<CustomerSummary> {
    const { data } = await apiClient.get<CustomerSummary>(
      withId(CUSTOMER_SUMMARY, userId)
    );
    return data;
  },

  async getMemberships(userId: number): Promise<{
    memberships: CustomerMembership[];
    transactions: CustomerMembershipTransaction[];
  }> {
    const { data } = await apiClient.get<{
      memberships: CustomerMembership[];
      transactions: CustomerMembershipTransaction[];
    }>(withId(CUSTOMER_MEMBERSHIPS, userId));
    return data;
  },

  async getStampCards(userId: number): Promise<CustomerStampProgress[]> {
    const { data } = await apiClient.get<CustomerStampProgress[]>(
      withId(CUSTOMER_STAMP_CARDS, userId)
    );
    return data;
  },

  /** Active vouchers; in branch view, of that branch (and company-wide). */
  async getVouchers(
    userId: number,
    branchId?: number | null
  ): Promise<CustomerVoucher[]> {
    const { data } = await apiClient.get<{ vouchers: CustomerVoucher[] }>(
      withId(CUSTOMER_VOUCHERS, userId),
      branchId ? { branchId } : {}
    );
    return data.vouchers ?? [];
  },

  /** Points history, 1-based pages. */
  async getPointsHistory(
    userId: number,
    companyId: number,
    page = 1,
    limit = 20
  ): Promise<{ transactions: PointsTransaction[]; total: number }> {
    const { data } = await apiClient.get<{
      transactions: PointsTransaction[];
      pagination: { total: number };
    }>(
      withId(CUSTOMER_POINTS_HISTORY, userId).replace(
        ':companyId',
        String(companyId)
      ),
      { page, limit }
    );
    return {
      transactions: data.transactions ?? [],
      total: data.pagination?.total ?? 0
    };
  },

  async addPoints(
    branchId: number,
    body: { userId: number; points: number; reason?: string }
  ): Promise<void> {
    await apiClient.post(
      LOYALTY_POINTS_ADD.replace(':branchId', String(branchId)),
      body
    );
  },

  async getVoucherTemplates(
    branchId: number
  ): Promise<VoucherTemplateOption[]> {
    const { data } = await apiClient.get<{ items: VoucherTemplateOption[] }>(
      VOUCHER_TEMPLATES_LIST,
      { branchId, status: 'active', page: 0, limit: 100 }
    );
    return data.items ?? [];
  },

  async grantVoucher(
    branchId: number,
    body: { userId: number; voucherTemplateId: number }
  ): Promise<{ code?: string }> {
    const { data } = await apiClient.post<{ voucher?: { code?: string } }>(
      `${VOUCHER_GRANT}/${branchId}`,
      body
    );
    return { code: data?.voucher?.code };
  }
};
